import { useState, useRef } from "react";
import { toast } from "sonner";
import { useLanguage } from "../../../i18n/LanguageContext";

const ALLOWED_TYPES = {
  'image/jpeg': 'image', 'image/png': 'image', 'image/gif': 'image',
  'image/webp': 'image', 'image/heic': 'image', 'image/heif': 'image',
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.ms-excel': 'xlsx',
};

const ACCEPT_STRING = Object.keys(ALLOWED_TYPES).join(',') + ',.jpg,.jpeg,.png,.gif,.webp,.pdf,.docx,.xlsx,.xls';

const FILE_ICONS = {
  image: '\uD83D\uDDBC',
  pdf: '\uD83D\uDCC4',
  docx: '\uD83D\uDCDD',
  xlsx: '\uD83D\uDCCA',
};

/** Free-tier default; prefer entitlements.max_files_per_case when available. */
export const MAX_FILES_PER_QUERY = 8;
export const MAX_FILES_PER_CASE_PAID = 20;
export const MAX_FILE_SIZE = 25 * 1024 * 1024;
export const MAX_TOTAL_UPLOAD_BYTES = 50 * 1024 * 1024;

const EXT_MAP = {
  jpg: 'image', jpeg: 'image', png: 'image', gif: 'image', webp: 'image',
  heic: 'image', heif: 'image', pdf: 'pdf', docx: 'docx', xlsx: 'xlsx', xls: 'xlsx',
};

function resolveFileType(file) {
  let fileType = ALLOWED_TYPES[file.type];
  if (!fileType) {
    const ext = file.name.split('.').pop().toLowerCase();
    fileType = EXT_MAP[ext];
  }
  return fileType;
}

/**
 * @param {{ maxFiles?: number }} [options]
 *   `maxFiles` from entitlements (case) or follow-up remaining pool.
 */
export function useFileUpload(options = {}) {
  const { t } = useLanguage();
  const maxFiles =
    typeof options.maxFiles === "number" && options.maxFiles >= 0
      ? options.maxFiles
      : MAX_FILES_PER_QUERY;
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const next = [...attachments];
    let total = next.reduce((sum, a) => sum + (a.size || 0), 0);
    let hitFileCap = false;
    let hitTotalCap = false;

    files.forEach((file) => {
      if (next.length >= maxFiles) {
        hitFileCap = true;
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(t('query.fileTooLarge'));
        return;
      }
      if (total + file.size > MAX_TOTAL_UPLOAD_BYTES) {
        hitTotalCap = true;
        return;
      }

      const fileType = resolveFileType(file);
      if (!fileType) {
        toast.error(`${t('query.unsupportedFile')}: ${file.name}`);
        return;
      }

      const attachment = {
        id: Date.now() + Math.random(),
        file: file,
        name: file.name,
        type: fileType,
        size: file.size,
        preview: null,
      };

      if (fileType === 'image') {
        const reader = new FileReader();
        reader.onload = (event) => {
          setAttachments((current) => current.map((a) =>
            a.id === attachment.id ? { ...a, preview: event.target.result } : a
          ));
        };
        reader.readAsDataURL(file);
      }

      next.push(attachment);
      total += file.size;
    });

    if (hitFileCap) {
      toast.error(t('query.tooManyFiles').replace('{max}', String(maxFiles)));
    }
    if (hitTotalCap) toast.error(t('query.attachmentsTooLarge'));
    setAttachments(next);
    e.target.value = '';
  };

  const removeAttachment = (id) => {
    setAttachments(prev => prev.filter(a => a.id !== id));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const clearAttachments = () => setAttachments([]);

  return {
    attachments,
    handleFileUpload,
    removeAttachment,
    formatFileSize,
    clearAttachments,
    fileInputRef,
    ACCEPT_STRING,
    FILE_ICONS,
    maxFiles,
  };
}
